import { useEffect, useMemo, useState } from 'react';
import { ChevronDown, ChevronRight, Folder as FolderIcon } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import { cn } from '@/lib/cn';
import type { Folder } from '@/types/api';

interface TreeNode {
  folder: Folder;
  children: TreeNode[];
}

function buildTree(folders: Folder[]): TreeNode[] {
  const byParent = new Map<string | null, Folder[]>();
  for (const f of folders.filter((f) => !f.is_archived)) {
    const key = f.parent_id ?? null;
    const list = byParent.get(key) ?? [];
    list.push(f);
    byParent.set(key, list);
  }
  for (const list of byParent.values()) {
    list.sort((a, b) => {
      const aOrd = a.sort_order ?? 0;
      const bOrd = b.sort_order ?? 0;
      if (aOrd !== bOrd) return aOrd - bOrd;
      return a.name.localeCompare(b.name);
    });
  }
  const make = (parent: string | null): TreeNode[] =>
    (byParent.get(parent) ?? []).map((f) => ({ folder: f, children: make(f.id) }));
  return make(null);
}

function computeAncestors(currentId: string | null | undefined, folders: Folder[]): Set<string> {
  if (!currentId) return new Set();
  const set = new Set<string>();
  const byId = new Map(folders.map((f) => [f.id, f]));
  let node = byId.get(currentId);
  while (node?.parent_id) {
    set.add(node.parent_id);
    node = byId.get(node.parent_id);
  }
  return set;
}

export function SidebarFolderTree({
  orgId,
  folders,
  onNavigate,
}: {
  orgId: string;
  folders: Folder[];
  onNavigate?: () => void;
}) {
  const location = useLocation();
  const match = location.pathname.match(/\/folders\/([^/]+)/);
  const currentFolderId = match?.[1] ?? null;

  const roots = useMemo(() => buildTree(folders), [folders]);
  const ancestorIds = useMemo(
    () => computeAncestors(currentFolderId, folders),
    [currentFolderId, folders],
  );

  if (roots.length === 0) {
    return (
      <p className="px-3 py-2 text-[11px] text-[#BABACE]">No folders yet</p>
    );
  }

  return (
    <ul className="pb-2">
      {roots.map((n) => (
        <SidebarTreeItem
          key={n.folder.id}
          node={n}
          orgId={orgId}
          depth={0}
          currentFolderId={currentFolderId}
          ancestorIds={ancestorIds}
          onNavigate={onNavigate}
        />
      ))}
    </ul>
  );
}

function SidebarTreeItem({
  node,
  orgId,
  depth,
  currentFolderId,
  ancestorIds,
  onNavigate,
}: {
  node: TreeNode;
  orgId: string;
  depth: number;
  currentFolderId: string | null;
  ancestorIds: Set<string>;
  onNavigate?: () => void;
}) {
  const [open, setOpen] = useState(
    depth < 1 || ancestorIds.has(node.folder.id),
  );

  useEffect(() => {
    if (ancestorIds.has(node.folder.id)) setOpen(true);
  }, [ancestorIds, node.folder.id]);

  const isActive = currentFolderId === node.folder.id;
  const hasChildren = node.children.length > 0;
  const indentPx = 10 + depth * 12;

  return (
    <li>
      <div
        className={cn(
          'flex items-center group',
          isActive
            ? 'bg-[#EFF0FF] dark:bg-[#1E2040] border-l-[3px] border-[#0410BD] dark:border-[#818CF8]'
            : 'border-l-[3px] border-transparent hover:bg-[#F2F2F8] dark:hover:bg-[#2A2A4A]',
        )}
        style={{ paddingLeft: Math.max(0, indentPx - 3) }}
      >
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          className="shrink-0 p-0.5 text-[#BABACE] hover:text-[#676687]"
          aria-label={open ? 'Collapse' : 'Expand'}
          disabled={!hasChildren}
        >
          {hasChildren ? (
            open ? <ChevronDown size={12} /> : <ChevronRight size={12} />
          ) : (
            <span className="inline-block w-3" />
          )}
        </button>
        <Link
          to={`/workspace/${orgId}/folders/${node.folder.id}`}
          onClick={onNavigate}
          title={node.folder.path || node.folder.name}
          className={cn(
            'flex-1 min-w-0 flex items-center gap-1.5 py-1.5 pr-2 text-[13px] truncate',
            isActive ? 'text-[#0410BD] dark:text-[#818CF8] font-medium' : 'text-[#12122C] dark:text-[#C4C4DC]',
          )}
        >
          <FolderIcon size={13} className="shrink-0 text-[#BABACE]" />
          <span className="truncate">{node.folder.name}</span>
        </Link>
      </div>
      {open && hasChildren && (
        <ul>
          {node.children.map((child) => (
            <SidebarTreeItem
              key={child.folder.id}
              node={child}
              orgId={orgId}
              depth={depth + 1}
              currentFolderId={currentFolderId}
              ancestorIds={ancestorIds}
              onNavigate={onNavigate}
            />
          ))}
        </ul>
      )}
    </li>
  );
}
